import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { Heading, Text } from 'react-aria-components';
import BackButton from 'src/components/BackButton';
import Tag from 'src/components/ui/Tag';
import TextLabel from 'src/components/ui/TextLabel';
import Button from 'src/components/ui/Button';
import ProgressBar from 'src/components/ui/ProgressBar';
import FileRow from 'src/components/student/FileRow';
import PageLoader from 'src/components/ui/PageLoader';
import Modal from 'src/components/ui/Modal';
import PasswordDialog from 'src/components/course/PasswordDialog';
import { Breadcrumb, Breadcrumbs } from 'src/components/ui/Breadcrumbs';
import ClockIcon from 'src/assets/icons/time-clock-circle.svg';
import CheckIcon from 'src/assets/icons/check-circle.svg';
import CreatorsIcon from 'src/assets/icons/creators.svg';
import ClassIcon from 'src/assets/icons/class-icon.svg';
import StartDateIcon from 'src/assets/icons/course-available-date.svg';
import COURSE_QUERY from 'src/graphql/query/course.graphql';

import type { Course, CourseMaterial } from 'src/types/course';

const CourseDetailsPage = () => {
  const { courseId } = Route.useParams();
  const [passVisible, setPassVisible] = useState(false);

  const { data, loading, error } = useQuery<{ course: Partial<Course> }>(
    COURSE_QUERY,
    { variables: { id: courseId } }
  );

  let isAvailable = true;
  let startDateTime = null;

  if (
    data?.course?.startDate &&
    DateTime.fromISO(data.course.startDate).isValid
  ) {
    startDateTime = DateTime.fromISO(data.course.startDate);
    const startSecondsLeft = startDateTime?.diffNow('seconds')?.seconds;

    isAvailable = startSecondsLeft <= 0;
  }

  if (loading) return <PageLoader />;
  if (!data?.course || error) return <Navigate to="/dashboard" />;

  return (
    <>
      {data.course?.password ? (
        <Modal
          size="small"
          isOpen={passVisible}
          isDismissable
          onOpenChange={setPassVisible}
          className="w-full max-w-[30rem]"
        >
          <PasswordDialog password={data.course.password} />
        </Modal>
      ) : null}

      <div className="flex flex-col items-start gap-1.5 px-6 pt-2.5 pb-1.5">
        <BackButton to="/dashboard" />
        <Breadcrumbs>
          <Breadcrumb href="/dashboard">Course Packs</Breadcrumb>
          <Breadcrumb className="flex-1 line-clamp-1">
            {data.course.name}
          </Breadcrumb>
        </Breadcrumbs>
      </div>
      <div className="bg-surface-alt rounded-3xl shadow-3 px-6 pt-4 pb-6">
        <Heading
          level={2}
          className="my-4 text-2xl text-surface-alt-90 font-bold"
        >
          {data.course.name}
        </Heading>

        <div className="flex items-center justify-start gap-4 flex-wrap">
          <TextLabel
            className="text-surface-75 py-1.5"
            Icon={ClockIcon}
            label={data.course.term?.name ?? '-'}
          />
          {data.course.subject?.name && (
            <Tag title={data.course.subject.name} />
          )}
        </div>

        <div className="mt-6 mb-4">
          <div className="flex items-center gap-4 py-1">
            <CreatorsIcon aria-hidden="true" className="w-6 h-6" />
            <Text className="text-sm text-surface-alt-75 font-medium">
              {(data.course?.professors ?? [])
                .map((p) => p.name)
                .filter(Boolean)
                .join(', ') || '-'}
            </Text>
          </div>
          <div className="flex items-center gap-4 py-1">
            <ClassIcon aria-hidden="true" className="w-6 h-6" />
            <Text className="text-sm text-surface-alt-75 font-medium">
              {data.course.identifier || '-'}
            </Text>
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-4 mb-11">
          <TextLabel label="Progress" Icon={CheckIcon} />
          <ProgressBar
            aria-label="Course Progress"
            value={Number(data.course.progress)}
          />
        </div>

        <section id="files">
          <Heading level={3} className="text-2xl text-surface-alt-90 font-bold">
            Course Materials
          </Heading>
          {!isAvailable ? (
            <div className="flex flex-col xs:flex-row max-w-[27.5rem] mx-auto mt-10 items-center gap-6 p-6 border border-surface-ghost rounded-[1.25rem]">
              <StartDateIcon className="w-10 h-10" />
              <Text className="text-center text-sm text-surface-alt-90 font-normal">
                The materials for this course will be available on{' '}
                <strong className="text-surface-alt-75 font-semibold">
                  {startDateTime?.toLocaleString(DateTime.DATE_SHORT)}
                </strong>
              </Text>
            </div>
          ) : (
            <div className="flex flex-col gap-1 mt-4 mb-4">
              {(data.course?.courseMaterials ?? []).map(
                (file: CourseMaterial) => (
                  <FileRow
                    key={file.courseMaterialId}
                    id={file.courseMaterialId}
                    courseId={data.course.courseId!}
                    name={file.name}
                    type={file.media}
                    downloadsAllowed={data.course.downloadsAllowed}
                    downloadsLimit={data.course.downloadsLimit}
                    onDownload={() => setPassVisible(true)}
                  />
                )
              )}
            </div>
          )}
          {Boolean(data?.course?.password) && (
            <div className="flex flex-col justify-between gap-4 mt-6 sm:flex-row sm:justify-between sm:items-center">
              <Text className="text-center sm:text-left text-surface-alt-90 text-sm font-medium">
                This course contains a material, which is copyrighted and
                requires password to be opened.
              </Text>
              <Button
                variant="secondary"
                ghost
                className="justify-center"
                onPress={() => setPassVisible(true)}
              >
                Show Password
              </Button>
            </div>
          )}
        </section>

        {data.course?.description ? (
          <section id="description" className="mt-16">
            <Heading
              level={3}
              className="text-2xl text-surface-alt-90 font-bold py-4"
            >
              About
            </Heading>
            <div className="text-surface-alt-90">{data.course.description}</div>
          </section>
        ) : null}
      </div>
    </>
  );
};

export const Route = createFileRoute(
  '/_authenticated/_layout-advanced/courses/$courseId/details'
)({
  component: CourseDetailsPage,
});
